import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Monitor, LayoutGrid, FileText, LogOut, Layers, CreditCard, Wand2,
  ShieldCheck, Sparkles, ImageIcon, FolderOpen, ListVideo, Grid3x3, Palette,
  MapPin, Menu, X, LifeBuoy, Coins, Wrench,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { NexsoLogo } from "@/components/app/NexsoLogo";
import SupportBubble from "@/components/app/SupportBubble";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const bodyFont    = { fontFamily: "Manrope, sans-serif" };

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Sidebar starts hidden on mobile, always visible on lg+.
  const [mobileOpen, setMobileOpen] = useState(false);

  // Auto-close the mobile drawer whenever the user navigates so they're not
  // stuck looking at the sidebar after picking a destination.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Lock body scroll while the mobile drawer is open so the page behind doesn't
  // scroll under your finger.
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navSections = [
    {
      label: "Manage",
      items: [
        { to: "/dashboard", label: "Overview", icon: LayoutGrid, exact: true },
        { to: "/dashboard/screens", label: "Screens", icon: Monitor },
        { to: "/dashboard/locations", label: "Locations", icon: MapPin },
        { to: "/dashboard/setup", label: "Setup a TV", icon: Wand2 },
        { to: "/dashboard/groups", label: "Groups", icon: Layers },
      ],
    },
    {
      label: "Create",
      items: [
        { to: "/dashboard/templates", label: "Templates", icon: LayoutGrid },
        { to: "/dashboard/brand-kit", label: "Brand Kit", icon: Palette },
        { to: "/dashboard/assets", label: "Media Library", icon: FolderOpen },
        { to: "/dashboard/playlists", label: "Playlists", icon: ListVideo },
        { to: "/dashboard/apps", label: "Apps", icon: Grid3x3 },
        { to: "/dashboard/banners", label: "Banner Studio", icon: ImageIcon },
        { to: "/dashboard/designs", label: "My Designs", icon: FileText },
      ],
    },
    {
      label: "Account",
      items: [
        { to: "/dashboard/credits", label: "Credits", icon: Coins },
        { to: "/dashboard/billing", label: "Billing", icon: CreditCard },
        { to: "/dashboard/support", label: "Help & Support", icon: LifeBuoy },
        ...(user?.role === "admin" ? [
          { to: "/dashboard/admin", label: "Admin", icon: ShieldCheck },
          { to: "/dashboard/admin/fleet", label: "Fleet · Remote", icon: Wrench },
        ] : []),
      ],
    },
  ];

  const effPlan = user?.effective_plan || user?.plan || "free";
  const isTrial = user?.is_trial;

  return (
    <div className="min-h-screen flex relative" style={{ ...bodyFont }} data-theme="light">
      <div className="absolute inset-0 nexso-app-bg pointer-events-none -z-10" />

      {/* Mobile top bar — only visible <lg */}
      <header
        className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 h-14 bg-white/95 backdrop-blur-sm border-b"
        style={{ borderColor: "var(--border-light)" }}
        data-testid="dashboard-mobile-header"
      >
        <button
          onClick={() => setMobileOpen(true)}
          className="h-10 w-10 -ml-2 rounded-lg flex items-center justify-center text-slate-700 hover:bg-slate-100 transition-colors"
          aria-label="Open menu"
          data-testid="open-sidebar-button"
        >
          <Menu className="h-5 w-5" />
        </button>
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link-mobile">
          <NexsoLogo size="sm" />
          <span className="font-semibold text-base tracking-tight text-slate-900" style={headingFont}>Nexso</span>
        </Link>
        {/* Spacer to balance the menu icon and keep the logo centered */}
        <div className="h-10 w-10" />
      </header>

      {/* Backdrop — covers main content when drawer is open on mobile */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-[1px]"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar — fixed slide-out on mobile, sticky column on lg+ */}
      <aside
        className={`
          fixed lg:sticky top-0 left-0 z-50
          h-screen lg:h-screen w-72 lg:w-64 lg:shrink-0
          border-r flex flex-col bg-white
          transition-transform duration-200 ease-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
        `}
        style={{ borderColor: "var(--border-light)" }}
        data-testid="dashboard-sidebar"
      >
        <div className="px-6 py-4 lg:py-5 border-b flex items-center justify-between" style={{ borderColor: "var(--border-light)" }}>
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
            <NexsoLogo size="md" className="shadow-[0_4px_18px_rgba(14,165,233,0.35)]" />
            <span className="font-semibold text-xl tracking-tight text-slate-900" style={headingFont}>Nexso</span>
          </Link>
          {/* Close button — mobile only */}
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden h-9 w-9 -mr-2 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close menu"
            data-testid="close-sidebar-button"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {navSections.map((section) => (
            <div key={section.label}>
              <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-400">{section.label}</div>
              <div className="space-y-0.5">
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.exact}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all ${
                        isActive
                          ? "bg-sky-50 text-sky-700 border border-sky-200/70"
                          : "text-slate-600 hover:bg-slate-50 hover:text-slate-900 border border-transparent"
                      }`
                    }
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div className="p-4 border-t" style={{ borderColor: "var(--border-light)" }}>
          <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 mb-1">Signed in as</div>
          <div className="text-sm font-medium truncate text-slate-900" data-testid="current-user-email">{user?.email}</div>
          <div className="mt-1.5">
            {effPlan === "pro" && !isTrial && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-sky-700 bg-sky-50 border border-sky-200 px-2 py-0.5 rounded-full" data-testid="user-plan-badge">
                <Sparkles className="h-3 w-3" /> Pro plan
              </span>
            )}
            {isTrial && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-sky-700 bg-sky-50 border border-sky-200 px-2 py-0.5 rounded-full" data-testid="user-plan-badge">
                <Sparkles className="h-3 w-3" /> Trial · Pro
              </span>
            )}
            {effPlan === "free" && !isTrial && (
              <span className="inline-flex text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full" data-testid="user-plan-badge">
                Free plan
              </span>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-4 justify-start gap-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-50 border-slate-200 bg-white"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </aside>

      <main className="relative flex-1 min-w-0 pt-14 lg:pt-0">{children}</main>
      <SupportBubble />
    </div>
  );
}
